<template>
  <div class="form-step">
    <!-- Education Section -->
    <div class="section-header d-flex justify-content-between align-items-center">
      <h5 class="section-title">Education</h5>
      <button type="button" class="btn btn-outline-primary" @click="addEducation">
        <i class="bi bi-plus me-2"></i>Add Education
      </button>
    </div>

    <div v-for="(edu, index) in education" :key="`edu-${educationKey(edu, index)}`" class="education-item mb-4 p-3 border rounded">
      <div
        class="education-header d-flex justify-content-between align-items-center"
        role="button"
        tabindex="0"
        @click="toggleEducation(educationKey(edu, index))"
        @keydown.enter.prevent="toggleEducation(educationKey(edu, index))"
        @keydown.space.prevent="toggleEducation(educationKey(edu, index))"
      >
        <h6 class="mb-0 border-0 pb-0">{{ educationHeaderTitles[educationKey(edu, index)] }}</h6>
        <i class="bi" :class="isEducationExpanded(educationKey(edu, index)) ? 'bi-chevron-up' : 'bi-chevron-down'"></i>
      </div>
      <div v-if="isEducationExpanded(educationKey(edu, index))" class="row my-3">
        <div class="col-md-4">
          <label class="form-label">Institution</label>
          <input
            type="text"
            class="form-control"
            v-model="edu.institution"
            @input="emitUpdate"
          >
        </div>
        <div class="col-md-4">
          <label class="form-label">Country</label>
          <select
            class="form-select"
            v-model="edu.country"
            @change="emitUpdate"
          >
            <option value="">Select Country</option>
            <option v-for="country in africanCountries" :key="country.value" :value="country.value">
              {{ country.label }}
            </option>
          </select>
        </div>
        <div class="col-md-4">
          <label class="form-label">Year</label>
          <input
            type="date"
            class="form-control"
            v-model="edu.year"
            @input="emitUpdate"
          >
        </div>
      </div>
      <div v-if="isEducationExpanded(educationKey(edu, index))" class="row mb-3">
        <div class="col-md-6">
          <label class="form-label">Degree</label>
          <input
            type="text"
            class="form-control"
            v-model="edu.degree"
            @input="emitUpdate"
          >
        </div>
        <div class="col-md-6">
          <label class="form-label">Keywords</label>
          <input
            type="text"
            class="form-control"
            v-model="edu.keywords"
            @input="emitUpdate"
          >
        </div>
      </div>
      <div v-if="isEducationExpanded(educationKey(edu, index))" class="row">
        <div class="col-md-12 d-flex justify-content-end">
          <button type="button" class="btn btn-sm btn-outline-danger" @click="removeEducation(index)">
            <i class="bi bi-x me-1"></i>Remove
          </button>
        </div>
      </div>
    </div>

    <!-- Board/Editorial Membership Section -->
    <div class="section-header d-flex justify-content-between align-items-center">
      <h5 class="section-title">Board/Editorial Membership</h5>
      <button type="button" class="btn btn-outline-primary" @click="addBoardMembership">
        <i class="bi bi-plus me-2"></i>Add Board/Editorial Membership
      </button>
    </div>

    <div v-for="(membership, index) in boardMemberships" :key="`board-${boardKey(membership, index)}`" class="board-item mb-4 p-3 border rounded">
      <div
        class="board-header d-flex justify-content-between align-items-center"
        role="button"
        tabindex="0"
        @click="toggleBoard(boardKey(membership, index))"
        @keydown.enter.prevent="toggleBoard(boardKey(membership, index))"
        @keydown.space.prevent="toggleBoard(boardKey(membership, index))"
      >
        <h6 class="mb-0 border-0 pb-0">{{ boardHeaderTitles[boardKey(membership, index)] }}</h6>
        <i class="bi" :class="isBoardExpanded(boardKey(membership, index)) ? 'bi-chevron-up' : 'bi-chevron-down'"></i>
      </div>
      <div v-if="isBoardExpanded(boardKey(membership, index))" class="row my-3">
        <div class="col-md-6">
          <label class="form-label">Committee/board</label>
          <input
            type="text"
            class="form-control"
            v-model="membership.committee"
            @input="emitUpdate"
          >
        </div>
        <div class="col-md-6">
          <label class="form-label">Role</label>
          <input
            type="text"
            class="form-control"
            v-model="membership.role"
            @input="emitUpdate"
          >
        </div>
      </div>
      <div v-if="isBoardExpanded(boardKey(membership, index))" class="row mb-3">
        <div class="col-md-6">
          <label class="form-label">Start Date</label>
          <input
            type="date"
            class="form-control"
            v-model="membership.startDate"
            @input="emitUpdate"
          >
        </div>
        <div class="col-md-6">
          <label class="form-label">End Date</label>
          <input
            type="date"
            class="form-control"
            v-model="membership.endDate"
            @input="emitUpdate"
          >
        </div>
      </div>
      <div v-if="isBoardExpanded(boardKey(membership, index))" class="row">
        <div class="col-md-12 d-flex justify-content-end">
          <button type="button" class="btn btn-sm btn-outline-danger" @click="removeBoardMembership(index)">
            <i class="bi bi-x me-1"></i>Remove
          </button>
        </div>
      </div>
    </div>

    <!-- Professional Positions Section -->
    <div class="section-header d-flex justify-content-between align-items-center">
      <h5 class="section-title">Professional Positions</h5>
      <button type="button" class="btn btn-outline-primary" @click="addProfessionalPosition">
        <i class="bi bi-plus me-2"></i>Add Professional Positions
      </button>
    </div>

    <div v-for="(position, index) in professionalPositions" :key="`position-${positionKey(position, index)}`" class="position-item mb-4 p-3 border rounded">
      <div
        class="position-header d-flex justify-content-between align-items-center"
        role="button"
        tabindex="0"
        @click="togglePosition(positionKey(position, index))"
        @keydown.enter.prevent="togglePosition(positionKey(position, index))"
        @keydown.space.prevent="togglePosition(positionKey(position, index))"
      >
        <h6 class="mb-0 border-0 pb-0">{{ positionHeaderTitles[positionKey(position, index)] }}</h6>
        <i class="bi" :class="isPositionExpanded(positionKey(position, index)) ? 'bi-chevron-up' : 'bi-chevron-down'"></i>
      </div>
      <div v-if="isPositionExpanded(positionKey(position, index))" class="row my-3">
        <div class="col-md-12">
          <label class="form-label">Position</label>
          <input
            type="text"
            class="form-control"
            v-model="position.position"
            @input="emitUpdate"
          >
        </div>
      </div>
      <div v-if="isPositionExpanded(positionKey(position, index))" class="row mb-3">
        <div class="col-md-6">
          <label class="form-label">Start Date</label>
          <input
            type="date"
            class="form-control"
            v-model="position.startDate"
            @input="emitUpdate"
          >
        </div>
        <div class="col-md-6">
          <label class="form-label">End Date</label>
          <input
            type="date"
            class="form-control"
            v-model="position.endDate"
            @input="emitUpdate"
          >
        </div>
      </div>
      <div v-if="isPositionExpanded(positionKey(position, index))" class="row">
        <div class="col-md-12 d-flex justify-content-end">
          <button type="button" class="btn btn-sm btn-outline-danger" @click="removeProfessionalPosition(index)">
            <i class="bi bi-x me-1"></i>Remove
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'EducationForm',

  props: {
    formData: {
      type: Object,
      required: true
    },
    profileData: {
      type: Object,
    }
  },
  emits: ['update'],
  data() {
    return {
      isoToName: {
        DZ: 'Algeria',
        AO: 'Angola',
        BJ: 'Benin',
        BW: 'Botswana',
        BF: 'Burkina Faso',
        BI: 'Burundi',
        CM: 'Cameroon',
        CV: 'Cape Verde',
        CF: 'Central African Republic',
        TD: 'Chad',
        KM: 'Comoros',
        CG: 'Republic of the Congo',
        CD: 'Democratic Republic of the Congo',
        DJ: 'Djibouti',
        EG: 'Egypt',
        GQ: 'Equatorial Guinea',
        ER: 'Eritrea',
        SZ: 'Eswatini',
        ET: 'Ethiopia',
        GA: 'Gabon',
        GM: 'Gambia',
        GH: 'Ghana',
        GN: 'Guinea',
        GW: 'Guinea-Bissau',
        CI: 'Ivory Coast',
        KE: 'Kenya',
        LS: 'Lesotho',
        LR: 'Liberia',
        LY: 'Libya',
        MG: 'Madagascar',
        MW: 'Malawi',
        ML: 'Mali',
        MR: 'Mauritania',
        MU: 'Mauritius',
        MA: 'Morocco',
        MZ: 'Mozambique',
        NA: 'Namibia',
        NE: 'Niger',
        NG: 'Nigeria',
        RW: 'Rwanda',
        ST: 'São Tomé & Príncipe',
        SN: 'Senegal',
        SC: 'Seychelles',
        SL: 'Sierra Leone',
        SO: 'Somalia',
        ZA: 'South Africa',
        SS: 'South Sudan',
        SD: 'Sudan',
        TZ: 'Tanzania',
        TG: 'Togo',
        TN: 'Tunisia',
        UG: 'Uganda',
        ZM: 'Zambia',
        ZW: 'Zimbabwe',
      },
      education: [],
      expandedEducationIds: [],
      expandedBoardIds: [],
      expandedPositionIds: [],
      boardMemberships: [],
      professionalPositions: [],
      initialized: false,
      africanCountries: [
        { value: 'DZ', label: 'Algeria' },
        { value: 'AO', label: 'Angola' },
        { value: 'BJ', label: 'Benin' },
        { value: 'BW', label: 'Botswana' },
        { value: 'BF', label: 'Burkina Faso' },
        { value: 'BI', label: 'Burundi' },
        { value: 'CM', label: 'Cameroon' },
        { value: 'CV', label: 'Cape Verde' },
        { value: 'CF', label: 'Central African Republic' },
        { value: 'TD', label: 'Chad' },
        { value: 'KM', label: 'Comoros' },
        { value: 'CG', label: 'Republic of the Congo (Brazzaville)' },
        { value: 'CD', label: 'Democratic Republic of the Congo (Kinshasa)' },
        { value: 'DJ', label: 'Djibouti' },
        { value: 'EG', label: 'Egypt' },
        { value: 'GQ', label: 'Equatorial Guinea' },
        { value: 'ER', label: 'Eritrea' },
        { value: 'SZ', label: 'Eswatini (Swaziland)' },
        { value: 'ET', label: 'Ethiopia' },
        { value: 'GA', label: 'Gabon' },
        { value: 'GM', label: 'Gambia' },
        { value: 'GH', label: 'Ghana' },
        { value: 'GN', label: 'Guinea' },
        { value: 'GW', label: 'Guinea-Bissau' },
        { value: 'CI', label: 'Ivory Coast (Côte d’Ivoire)' },
        { value: 'KE', label: 'Kenya' },
        { value: 'LS', label: 'Lesotho' },
        { value: 'LR', label: 'Liberia' },
        { value: 'LY', label: 'Libya' },
        { value: 'MG', label: 'Madagascar' },
        { value: 'MW', label: 'Malawi' },
        { value: 'ML', label: 'Mali' },
        { value: 'MR', label: 'Mauritania' },
        { value: 'MU', label: 'Mauritius' },
        { value: 'MA', label: 'Morocco' },
        { value: 'MZ', label: 'Mozambique' },
        { value: 'NA', label: 'Namibia' },
        { value: 'NE', label: 'Niger' },
        { value: 'NG', label: 'Nigeria' },
        { value: 'RW', label: 'Rwanda' },
        { value: 'ST', label: 'São Tomé & Príncipe' },
        { value: 'SN', label: 'Senegal' },
        { value: 'SC', label: 'Seychelles' },
        { value: 'SL', label: 'Sierra Leone' },
        { value: 'SO', label: 'Somalia' },
        { value: 'ZA', label: 'South Africa' },
        { value: 'SS', label: 'South Sudan' },
        { value: 'SD', label: 'Sudan' },
        { value: 'TZ', label: 'Tanzania' },
        { value: 'TG', label: 'Togo' },
        { value: 'TN', label: 'Tunisia' },
        { value: 'UG', label: 'Uganda' },
        { value: 'ZM', label: 'Zambia' },
        { value: 'ZW', label: 'Zimbabwe' },
      ],
    }
  },
  computed: {
    educationHeaderTitles() {
      return this.education.reduce((titles, edu, index) => {
        const key = this.educationKey(edu, index)
        const degree = this.normalizeHeaderText(edu?.degree)
        const institution = this.normalizeHeaderText(edu?.institution)
        const yearValue = typeof edu?.year === 'string' ? edu.year.trim() : ''
        const yearMatch = yearValue.match(/^(\d{4})/)
        const year = yearMatch ? yearMatch[1] : ''

        if (!degree && !institution && !year) {
          titles[key] = 'New Education'
          return titles
        }

        titles[key] = [degree, institution, year].filter(Boolean).join(', ')
        return titles
      }, {})
    },
    boardHeaderTitles() {
      return this.boardMemberships.reduce((titles, membership, index) => {
        const key = this.boardKey(membership, index)
        const role = this.normalizeHeaderText(membership?.role)
        const committee = this.normalizeHeaderText(membership?.committee)
        const startDateValue = typeof membership?.startDate === 'string' ? membership.startDate.trim() : ''
        const yearMatch = startDateValue.match(/^(\d{4})/)
        const year = yearMatch ? yearMatch[1] : ''

        if (!role && !committee && !year) {
          titles[key] = 'New Board Membership'
          return titles
        }

        titles[key] = [role, committee, year].filter(Boolean).join(', ')
        return titles
      }, {})
    },
    positionHeaderTitles() {
      return this.professionalPositions.reduce((titles, position, index) => {
        const key = this.positionKey(position, index)
        const positionName = this.normalizeHeaderText(position?.position)
        const startDateValue = typeof position?.startDate === 'string' ? position.startDate.trim() : ''
        const yearMatch = startDateValue.match(/^(\d{4})/)
        const year = yearMatch ? yearMatch[1] : ''

        if (!positionName && !year) {
          titles[key] = 'New Professional Position'
          return titles
        }

        titles[key] = [positionName, year].filter(Boolean).join(', ')
        return titles
      }, {})
    }
  },
  watch: {
    // FIXED: Watch for changes in formData and initialize properly
    formData: {
      handler(newData) {
        if (newData && !this.initialized) {
          this.initializeData()
        }
      },
      deep: true,
      immediate: true
    },

    // FIXED: Watch for profileData changes
    profileData: {
      handler(newData) {
        if (newData && !this.initialized) {
          this.initializeData()
        }
      },
      deep: true,
      immediate: true
    }
  },
  methods: {
    normalizeHeaderText(value) {
      if (typeof value !== 'string') return ''

      const cleaned = value.trim().replace(/\s+/g, ' ')
      if (!cleaned) return ''

      return cleaned
        .toLowerCase()
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ')
    },

    // FIXED: Better initialization logic
    initializeData() {
      this.education = []
      this.boardMemberships = []
      this.professionalPositions = []

      // Initialize from formData first (highest priority)
      if (this.formData?.education && Array.isArray(this.formData.education)) {
        this.education = this.formData.education.map(edu => ({
          id: edu.id || null,
          _localKey: edu.id ? null : `${Date.now()}-${Math.random()}`,
          _isNew: false,
          institution: edu.institution || '',
          degree: edu.degree || '',
          country: this.convertCountry(edu.country),
          year: edu.year || '',
          keywords: edu.keywords || ''
        }))
      }

      if (this.formData?.boardMemberships && Array.isArray(this.formData.boardMemberships)) {
        this.boardMemberships = this.formData.boardMemberships.map(membership => ({
          id: membership.id || null,
          _localKey: membership.id ? null : `${Date.now()}-${Math.random()}`,
          _isNew: false,
          committee: membership.committee || '',
          role: membership.role || '',
          startDate: membership.startDate || '',
          endDate: membership.endDate || ''
        }))
      }

      if (this.formData?.professionalPositions && Array.isArray(this.formData.professionalPositions)) {
        this.professionalPositions = this.formData.professionalPositions.map(position => ({
          id: position.id || null,
          _localKey: position.id ? null : `${Date.now()}-${Math.random()}`,
          _isNew: false,
          position: position.position || '',
          startDate: position.startDate || '',
          endDate: position.endDate || ''
        }))
      }

      // If no data in formData, try to get from profileData
      if (this.profileData?.data) {
        const data = this.profileData.data

        // Load education if not already loaded
        if (this.education.length === 0 && data.education && Array.isArray(data.education)) {
          this.education = data.education.map(edu => ({
            id: edu.id || null,
            _localKey: edu.id ? null : `${Date.now()}-${Math.random()}`,
            _isNew: false,
            institution: edu.institution || '',
            degree: edu.degree || '',
            country: edu.country || '',
            year: edu.year || '',
            keywords: edu.keywords || ''
          }))
        }

        // Load board memberships if not already loaded
        if (this.boardMemberships.length === 0 && data.boardMemberships && Array.isArray(data.boardMemberships)) {
          this.boardMemberships = data.boardMemberships.map(membership => ({
            id: membership.id || null,
            _localKey: membership.id ? null : `${Date.now()}-${Math.random()}`,
            _isNew: false,
            committee: membership.committee || '',
            role: membership.role || '',
            startDate: membership.startDate || '',
            endDate: membership.endDate || ''
          }))
        }

        // Load professional positions if not already loaded
        if (this.professionalPositions.length === 0 && data.professionalPositions && Array.isArray(data.professionalPositions)) {
          this.professionalPositions = data.professionalPositions.map(position => ({
            id: position.id || null,
            _localKey: position.id ? null : `${Date.now()}-${Math.random()}`,
            _isNew: false,
            position: position.position || '',
            startDate: position.startDate || '',
            endDate: position.endDate || ''
          }))
        }
      }

      this.initialized = true
      this.expandedEducationIds = this.education
        .map((edu, index) => (edu._isNew ? this.educationKey(edu, index) : null))
        .filter(Boolean)
      this.expandedBoardIds = this.boardMemberships
        .map((membership, index) => (membership._isNew ? this.boardKey(membership, index) : null))
        .filter(Boolean)
      this.expandedPositionIds = this.professionalPositions
        .map((position, index) => (position._isNew ? this.positionKey(position, index) : null))
        .filter(Boolean)

      // Emit the initial data
      this.emitUpdate()
    },

    // Emit current data to parent
    emitUpdate() {
      const data = {
        education: this.education,
        boardMemberships: this.boardMemberships,
        professionalPositions: this.professionalPositions
      }

      this.$emit('update', data)
    },

    // Education methods
    addEducation() {
      const newEducation = {
        id: null,
        _localKey: `${Date.now()}-${Math.random()}`,
        _isNew: true,
        institution: '',
        degree: '',
        country: '',
        year: '',
        keywords: ''
      }

      this.education.push(newEducation)
      const key = this.educationKey(newEducation, this.education.length - 1)
      if (key && !this.expandedEducationIds.includes(key)) {
        this.expandedEducationIds.push(key)
      }
      this.emitUpdate()
    },

    removeEducation(index) {
      const removed = this.education[index]
      this.education.splice(index, 1)
      const removedKey = this.educationKey(removed, index)
      this.expandedEducationIds = this.expandedEducationIds.filter(id => id !== removedKey)
      this.emitUpdate()
    },

    educationKey(education, index) {
      if (!education) return `new-${index}`
      return education.id || education._localKey || `new-${index}`
    },

    toggleEducation(educationKey) {
      if (!educationKey) return

      if (this.expandedEducationIds.includes(educationKey)) {
        this.expandedEducationIds = this.expandedEducationIds.filter(id => id !== educationKey)
      } else {
        this.expandedEducationIds.push(educationKey)
      }
    },

    isEducationExpanded(educationKey) {
      return this.expandedEducationIds.includes(educationKey)
    },

    // Board membership methods
    addBoardMembership() {
      const newMembership = {
        id: null,
        _localKey: `${Date.now()}-${Math.random()}`,
        _isNew: true,
        committee: '',
        role: '',
        startDate: '',
        endDate: ''
      }

      this.boardMemberships.push(newMembership)
      const key = this.boardKey(newMembership, this.boardMemberships.length - 1)
      if (key && !this.expandedBoardIds.includes(key)) {
        this.expandedBoardIds.push(key)
      }
      this.emitUpdate()
    },

    removeBoardMembership(index) {
      const removed = this.boardMemberships[index]
      this.boardMemberships.splice(index, 1)
      const removedKey = this.boardKey(removed, index)
      this.expandedBoardIds = this.expandedBoardIds.filter(id => id !== removedKey)
      this.emitUpdate()
    },

    boardKey(membership, index) {
      if (!membership) return `new-${index}`
      return membership.id || membership._localKey || `new-${index}`
    },

    toggleBoard(boardKey) {
      if (!boardKey) return

      if (this.expandedBoardIds.includes(boardKey)) {
        this.expandedBoardIds = this.expandedBoardIds.filter(id => id !== boardKey)
      } else {
        this.expandedBoardIds.push(boardKey)
      }
    },

    isBoardExpanded(boardKey) {
      return this.expandedBoardIds.includes(boardKey)
    },

    // Professional position methods
    addProfessionalPosition() {
      const newPosition = {
        id: null,
        _localKey: `${Date.now()}-${Math.random()}`,
        _isNew: true,
        position: '',
        startDate: '',
        endDate: ''
      }

      this.professionalPositions.push(newPosition)
      const key = this.positionKey(newPosition, this.professionalPositions.length - 1)
      if (key && !this.expandedPositionIds.includes(key)) {
        this.expandedPositionIds.push(key)
      }
      this.emitUpdate()
    },

    removeProfessionalPosition(index) {
      const removed = this.professionalPositions[index]
      this.professionalPositions.splice(index, 1)
      const removedKey = this.positionKey(removed, index)
      this.expandedPositionIds = this.expandedPositionIds.filter(id => id !== removedKey)
      this.emitUpdate()
    },

    positionKey(position, index) {
      if (!position) return `new-${index}`
      return position.id || position._localKey || `new-${index}`
    },

    togglePosition(positionKey) {
      if (!positionKey) return

      if (this.expandedPositionIds.includes(positionKey)) {
        this.expandedPositionIds = this.expandedPositionIds.filter(id => id !== positionKey)
      } else {
        this.expandedPositionIds.push(positionKey)
      }
    },

    isPositionExpanded(positionKey) {
      return this.expandedPositionIds.includes(positionKey)
    },

    convertIsoCountry(isoCode) {
      if (!isoCode) return '';

      const name = this.isoToName[isoCode];
      if (!name) return '';

      // Find matching dropdown entry
      const match = this.africanCountries.find(
        c => c.label.toLowerCase() === name.toLowerCase()
      );

      return match ? match.value : '';
    },

    convertCountry(value) {
      if (!value) return '';

      value = String(value).trim();

      // ISO code match
      const upper = value.toUpperCase();
      if (this.isoToName[upper]) return upper;

      // Normalize word names
      const normalized = value.replace(/-/g, ' ').toLowerCase().trim();

      // Exact label match
      const exact = this.africanCountries.find(
        c => c.label.toLowerCase() === normalized
      );
      if (exact) return exact.value;

      // Fuzzy match
      const fuzzy = this.africanCountries.find(
        c => normalized.includes(c.label.toLowerCase())
      );
      if (fuzzy) return fuzzy.value;

      return '';
    }

  },

  // FIXED: Initialize on mount
  mounted() {
    this.$nextTick(() => {
      this.initializeData()
    })
  }
}
</script>

<style scoped>
.form-step {
  min-height: 400px;
}

.form-label {
  font-weight: 500;
  margin-bottom: 0.5rem;
}

.form-control,
.form-select {
  border-radius: 6px;
  border: 1px solid #dee2e6;
  padding: 0.75rem;
}

.form-control:focus,
.form-select:focus {
  border-color: #17a2b8;
  box-shadow: 0 0 0 0.2rem rgba(23, 162, 184, 0.25);
}

.education-item,
.board-item,
.position-item {
  background-color: #f8f9fa;
  border: 1px solid #dee2e6;
}

.education-header {
  cursor: pointer;
  user-select: none;
}

.board-header {
  cursor: pointer;
  user-select: none;
}

.position-header {
  cursor: pointer;
  user-select: none;
}

h6 {
  color: #495057;
  border-bottom: 1px solid #dee2e6;
  padding-bottom: 0.5rem;
}

.btn-outline-danger {
  border-color: #dc3545;
  color: #dc3545;
}

.btn-outline-danger:hover {
  background-color: #dc3545;
  color: white;
}

.section-header {
  margin: 2rem 0 1.5rem 0;
  padding-bottom: 0.75rem;
  border-bottom: 2px solid #17a2b8;
}

.section-title {
  color: #17a2b8;
  font-weight: 600;
  font-size: 1.1rem;
  margin: 0;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}
</style>
